import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BottomTabBar } from '@/components/layout/BottomTabBar'
import { SUBJECTS } from '@krudream/shared'
import { apiUrl } from '@/lib/api'

export function HomeworkFormPage() {
  const navigate = useNavigate()
  const [form, setForm] = useState({
    subject: '',
    title: '',
    description: '',
    dueDate: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  function validate(): boolean {
    const errs: Record<string, string> = {}
    if (!form.subject) errs.subject = 'กรุณาเลือกวิชา'
    if (!form.title.trim()) errs.title = 'กรุณากรอกชื่อการบ้าน'
    if (!form.dueDate) errs.dueDate = 'กรุณาเลือกวันส่ง'
    else if (form.dueDate < new Date().toISOString().split('T')[0]) {
      errs.dueDate = 'วันส่งต้องไม่เป็นอดีต'
    }
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return
    setIsSubmitting(true)

    try {
      const res = await fetch(apiUrl('/api/homework'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: form.subject,
          title: form.title.trim(),
          description: form.description.trim() || undefined,
          dueDate: form.dueDate,
        }),
      })
      const data = await res.json()
      if (data.success) {
        setToast('สร้างการบ้านสำเร็จ')
        setTimeout(() => navigate('/homework'), 800)
      } else {
        setToast(data.error?.messageTh || 'เกิดข้อผิดพลาด')
      }
    } catch {
      setToast('เกิดข้อผิดพลาด')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <header className="bg-white px-4 py-3 shadow-sm">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-gray-600">⟨</button>
          <h1 className="text-lg font-bold text-gray-900">สร้างการบ้าน</h1>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Subject */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">วิชา *</label>
            <select
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className={`w-full rounded-lg border px-3 py-2.5 text-sm ${
                errors.subject ? 'border-red-300' : 'border-gray-200'
              } focus:border-primary-500 focus:outline-none`}
            >
              <option value="">เลือกวิชา</option>
              {SUBJECTS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            {errors.subject && <p className="mt-1 text-xs text-red-500">{errors.subject}</p>}
          </div>

          {/* Title */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">หัวข้อ *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="เช่น แบบฝึกหัดบทที่ 3 หน้า 45-48"
              className={`w-full rounded-lg border px-3 py-2.5 text-sm ${
                errors.title ? 'border-red-300' : 'border-gray-200'
              } focus:border-primary-500 focus:outline-none`}
            />
            {errors.title && <p className="mt-1 text-xs text-red-500">{errors.title}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              รายละเอียด <span className="text-gray-400">(ไม่บังคับ)</span>
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-primary-500 focus:outline-none"
            />
          </div>

          {/* Due date */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">วันส่ง *</label>
            <input
              type="date"
              value={form.dueDate}
              onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
              className={`w-full rounded-lg border px-3 py-2.5 text-sm ${
                errors.dueDate ? 'border-red-300' : 'border-gray-200'
              } focus:border-primary-500 focus:outline-none`}
            />
            {errors.dueDate && <p className="mt-1 text-xs text-red-500">{errors.dueDate}</p>}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-lg bg-primary-600 py-3 text-sm font-semibold text-white disabled:opacity-50"
          >
            {isSubmitting ? 'กำลังบันทึก...' : 'สร้างการบ้าน'}
          </button>
        </form>
      </main>

      {toast && (
        <div className="fixed left-1/2 top-4 z-50 -translate-x-1/2 rounded-lg bg-gray-800 px-4 py-2 text-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      <BottomTabBar active="homework" />
    </div>
  )
}
